import apiClient from '@/lib/api';

export type PlatformFunnel = 'signup' | 'register';
export type PlatformCapiEvent = 'Lead' | 'StartTrial' | 'Subscribe';

export const PLATFORM_FUNNELS: { value: PlatformFunnel; label: string; hint: string }[] = [
  { value: 'signup', label: '/signup', hint: 'Paid plans only' },
  { value: 'register', label: '/register', hint: 'Paid plans only (Free plan hidden)' },
];
export const PLATFORM_EVENTS: { value: PlatformCapiEvent; label: string; hint: string }[] = [
  { value: 'Lead', label: 'Lead', hint: 'Account created (email or social)' },
  { value: 'StartTrial', label: 'StartTrial', hint: 'Trial started at checkout or from Billing' },
  { value: 'Subscribe', label: 'Subscribe', hint: 'Paid subscription started: immediate charge or trial converted' },
];

export interface AdminPixel {
  id: number;
  label: string;
  pixelId: string;
  hasAccessToken: boolean;
  accessTokenLast4: string | null;
  testEventCode: string | null;
  enabled: boolean;
  funnels: PlatformFunnel[];
  events: PlatformCapiEvent[];
  lastSentAtUtc: string | null;
  lastError: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface AdminPixelInput {
  label: string;
  pixelId: string;
  /** Omit or send '' on update to keep the stored token. */
  accessToken?: string;
  testEventCode: string | null;
  enabled: boolean;
  funnels: PlatformFunnel[];
  events: PlatformCapiEvent[];
}

export interface AdminPixelTestResult {
  ok: boolean;
  httpStatus: number;
  body: unknown;
  testEventCode: string;
}

export type TrackingLogStatus = 'success' | 'client_error' | 'server_error' | 'timeout';

export interface AdminTrackingLog {
  id: string;
  deliveryId: string;
  status: TrackingLogStatus;
  httpStatus: number | null;
  action: string;
  eventName: string | null;
  funnel: PlatformFunnel | null;
  pixelId: string | null;
  pixelLabel: string | null;
  isTest: boolean;
  agency: { publicId: string; name: string } | null;
  reference: string | null;
  error: string | null;
  request: unknown;
  response: unknown;
  createdAtUtc: string;
}

export interface TrackingLogFilters {
  page?: number;
  limit?: number;
  pixelId?: string;
  event?: string;
  status?: string;
  funnel?: string;
  /** Matches the event id or the agency name. */
  search?: string;
}

export interface TrackingLogMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const trackingAdminService = {
  listPixels: async (): Promise<AdminPixel[]> => {
    const res = await apiClient.get<{ data: AdminPixel[] }>('/admin/tracking/pixels');
    return res.data.data;
  },
  createPixel: async (body: AdminPixelInput): Promise<AdminPixel> => {
    const res = await apiClient.post<{ data: AdminPixel }>('/admin/tracking/pixels', body);
    return res.data.data;
  },
  updatePixel: async (id: number, body: Partial<AdminPixelInput>): Promise<AdminPixel> => {
    const res = await apiClient.patch<{ data: AdminPixel }>(`/admin/tracking/pixels/${id}`, body);
    return res.data.data;
  },
  deletePixel: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/tracking/pixels/${id}`);
  },
  testPixel: async (id: number): Promise<AdminPixelTestResult> => {
    const res = await apiClient.post<{ data: AdminPixelTestResult }>(`/admin/tracking/pixels/${id}/test`);
    return res.data.data;
  },
  /** The decrypted access token. POST so read-only roles are refused server-side. */
  revealToken: async (id: number): Promise<string> => {
    const res = await apiClient.post<{ data: { accessToken: string } }>(`/admin/tracking/pixels/${id}/token`);
    return res.data.data.accessToken;
  },
  listLogs: async (filters: TrackingLogFilters = {}): Promise<{ data: AdminTrackingLog[]; meta: TrackingLogMeta }> => {
    const params = new URLSearchParams();
    params.set('page', String(filters.page ?? 1));
    params.set('limit', String(filters.limit ?? 25));
    if (filters.pixelId) params.set('pixelId', filters.pixelId);
    if (filters.event) params.set('event', filters.event);
    if (filters.status) params.set('status', filters.status);
    if (filters.funnel) params.set('funnel', filters.funnel);
    if (filters.search) params.set('search', filters.search);
    const res = await apiClient.get<{ data: AdminTrackingLog[]; meta: TrackingLogMeta }>(`/admin/tracking/logs?${params.toString()}`);
    return res.data;
  },
};
